// Iter-278.K — Sentry observability bootstrap.
//
// Why now?
//   - Iter-278.J + BUSINESS_FORECAST_24_MONTHS.md item #2: a site with
//     250 backend endpoints and 100+ frontend routes has no business
//     running in production without an error-tracking surface. We are
//     "operating blind" without it.
//
// Why this implementation?
//   - Fail-safe no-op. If REACT_APP_SENTRY_DSN is unset (preview, local
//     dev, or any deployment that hasn't configured it yet), this file
//     does NOTHING. No init, no network calls, no console warnings.
//   - Lazy-init. Sentry's SDK is imported at the top of the bundle but
//     only registers when a DSN is present.
//   - Sample rates kept LOW by default to avoid burning the Sentry
//     quota during ramp; admins should tune up via env var as scale
//     warrants.
//
// To enable in production:
//   1. Create a Sentry project (or use an existing one).
//   2. Copy the DSN.
//   3. Set REACT_APP_SENTRY_DSN=https://<key>@<org>.ingest.sentry.io/<project>
//      in /app/frontend/.env (or your deploy env).
//   4. Optional: set REACT_APP_SENTRY_TRACES_SAMPLE_RATE (default 0.05).
//   5. Restart the frontend. Throw a sample error from the console to
//      verify ingest:
//        Sentry.captureException(new Error("CarSavr Sentry smoke test"));
//
// Removing Sentry entirely:
//   Delete this file's import line in /app/frontend/src/index.js. The
//   yarn package can stay (tree-shaking will drop it).

import * as Sentry from "@sentry/react";

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || "preview";
const TRACES_RATE = Number.parseFloat(
  process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.05",
);
const REPLAYS_ON_ERROR_RATE = Number.parseFloat(
  process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR || "0",
);

let _initialized = false;

export function initSentry() {
  if (_initialized) return false;
  if (!DSN) {
    if (typeof window !== "undefined" && window.console && process.env.NODE_ENV !== "production") {
      // Dev-only soft hint. Production should NOT log this — assume
      // operators looked at the README before deploying.
      window.console.debug(
        "[Sentry] REACT_APP_SENTRY_DSN not set; error tracking is disabled. " +
          "See /app/frontend/src/lib/sentry.js for setup.",
      );
    }
    return false;
  }

  try {
    Sentry.init({
      dsn: DSN,
      environment: ENV,
      // Iter-416 — release tagging so regressions can be pinned to a
      // deploy and uploaded source maps resolve. Set
      // REACT_APP_SENTRY_RELEASE at build time (e.g. the git SHA).
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      // Iter-278.K — start conservative. Tune up once we have a
      // baseline of which routes need traces.
      tracesSampleRate: Number.isFinite(TRACES_RATE) ? TRACES_RATE : 0.05,
      // Session replay only for errored sessions by default — costly
      // otherwise. Set REACT_APP_SENTRY_REPLAYS_ON_ERROR=1.0 to enable
      // 100% capture of error sessions.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: Number.isFinite(REPLAYS_ON_ERROR_RATE)
        ? REPLAYS_ON_ERROR_RATE
        : 0,
      // Don't leak PII unless explicitly enabled via Sentry project
      // settings. Defense-in-depth: also scrub the URL search string.
      sendDefaultPii: false,
      beforeSend(event) {
        try {
          if (event.request && event.request.url) {
            const u = new URL(event.request.url);
            // Strip ZIPs / emails / tokens that may have leaked into URLs.
            for (const k of ["email", "zip", "token", "code", "session"]) {
              if (u.searchParams.has(k)) u.searchParams.set(k, "[redacted]");
            }
            event.request.url = u.toString();
          }
        } catch (_) {
          /* best-effort */
        }
        return event;
      },
      // Iter-278.K — Skip known-noisy errors that don't actionably
      // help us. Tune as the signal/noise ratio becomes clearer.
      ignoreErrors: [
        // Browser extensions
        "top.GLOBALS",
        // Network noise users can't do anything about
        "Network request failed",
        "Load failed",
        // Resize observer benign warnings
        "ResizeObserver loop completed with undelivered notifications",
        "ResizeObserver loop limit exceeded",
        // CSP-blocked third-party scripts
        /Script error\.?/,
      ],
    });
    _initialized = true;
    return true;
  } catch (err) {
    // If Sentry itself errors during init, never let it crash the app.
    if (typeof window !== "undefined" && window.console) {
      window.console.warn("[Sentry] init failed:", err && err.message);
    }
    return false;
  }
}

export { Sentry };
